import React, { createContext, useContext } from 'react'
import { useSession } from '../services/auth-client.ts'

interface User {
  id: string
  name: string
  email: string
  role?: string
  image?: string | null
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  user: User | null
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { data: session, isPending, error: sessionError, refetch } = useSession()

  const isAuthenticated = !!session?.user
  const isLoading = isPending

  const user: User | null = session?.user
    ? {
        id: session.user.id,
        name: session.user.name,
        email: session.user.email,
        image: session.user.image,
      }
    : null

  const error = sessionError ? 'Failed to verify session.' : null

  const checkAuth = async () => {
    await refetch()
  }

  const value = {
    isAuthenticated,
    isLoading,
    error,
    user,
    checkAuth,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
